import React, { memo, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, {
  Extrapolation,
  interpolate,
  type SharedValue,
  useAnimatedStyle,
} from 'react-native-reanimated';
import { appTheme, useThemeColors, type ThemeColors } from '../../../shared/theme';
import type { MenuFlowSection } from '../../../data/domains/foodMenu';
import { MenuFlowSectionHeader, SECTION_HEADER_HEIGHT } from '../../components/MenuFlowSectionHeader';
import { MENU_SET2_SECTION_HEADER_GAP, MENU_SET2_SECTION_LIST_HEADER_ROW } from '../../components/menuFlowHeaderLayout';

/** Distance (px) over which the list bar shrinks/fades into the sticky header. */
const DOCK_RANGE = 56;

type Props = {
  section: MenuFlowSection;
  /** Absolute content Y of this row (from list model offsets). */
  rowOffset: number;
  scrollY: SharedValue<number>;
  /** Viewport Y of sticky section chip top (inset + live header − bottom pad − chip). */
  stickyChipTopSV: SharedValue<number>;
};

function createStyles(c: ThemeColors) {
  return StyleSheet.create({
    row: {
      height: MENU_SET2_SECTION_LIST_HEADER_ROW,
      justifyContent: 'center',
      paddingVertical: MENU_SET2_SECTION_HEADER_GAP,
      paddingHorizontal: appTheme.spacing.lg,
    },
    chip: {
      height: SECTION_HEADER_HEIGHT,
      borderRadius: SECTION_HEADER_HEIGHT / 2,
      backgroundColor: c.surface,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: c.border,
      overflow: 'hidden',
    },
  });
}

/**
 * set2 in-list section bar — docks into the sticky header with scale + opacity as it nears.
 */
function MenuFlowSectionListBarSet2Component({
  section,
  rowOffset,
  scrollY,
  stickyChipTopSV,
}: Props) {
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createStyles(colors), [colors]);

  const dock_style = useAnimatedStyle(() => {
    'worklet';
    const sticky_content_y = scrollY.value + stickyChipTopSV.value;
    const dist = rowOffset - sticky_content_y;
    // 0 = far below (full), 1 = at/above sticky chip (absorbed).
    const dock = interpolate(dist, [DOCK_RANGE, 0], [0, 1], Extrapolation.CLAMP);
    return {
      opacity: interpolate(dock, [0, 1], [1, 0.12], Extrapolation.CLAMP),
      transform: [{ scale: interpolate(dock, [0, 1], [1, 0.88], Extrapolation.CLAMP) }],
    };
  });

  return (
    <View style={styles.row}>
      <Animated.View style={[styles.chip, dock_style]}>
        <MenuFlowSectionHeader currentSection={section} chrome_variant="set2" count_chrome="chip" />
      </Animated.View>
    </View>
  );
}

export const MenuFlowSectionListBarSet2 = memo(MenuFlowSectionListBarSet2Component);
